import { SelectOption } from 'global';
import { v4 as uuidv4 } from 'uuid';
import { CustomerVisibility } from 'modules/dealSheet';
import { QuoteFeeType } from '@trnsact/trnsact-shared-types';
import { Charge, ChargeAttachType, ChargeBucketRef, ChargeParentKind } from 'modules/equipment/types';

import { chargeTypesByLabels, generateEquipmentName, newChargeRow } from '../../equipment';
import { InvoiceListingBucket, QuoteChargeListingEntity, QuoteLineItemType } from '../types';

interface BuildParentOptionsParams {
  currentRowIndex: number;
  scope: ChargeBucketRef;
  rows: QuoteChargeListingEntity[];
}

export interface ChargeParentOption extends SelectOption {
  kind: ChargeParentKind;
}

export class ChargeListingService {
  public resolveParentScope(row: QuoteChargeListingEntity, invoiceId: string) {
    if (row.type === QuoteLineItemType.Equipment) {
      const equipmentId = row.equipment?.equipmentId;

      return equipmentId ? { id: equipmentId, type: ChargeAttachType.Equipment } : null;
    }

    const bucket = row.charge?.groupBucket;

    if (!bucket) return null;

    if (bucket.type === ChargeAttachType.Invoice) {
      return { id: bucket.id || invoiceId, type: ChargeAttachType.Invoice };
    }

    return bucket;
  }

  public buildBuckets(rows: QuoteChargeListingEntity[], invoiceId: string): InvoiceListingBucket[] {
    const equipmentBuckets: InvoiceListingBucket[] = [];

    const invoiceBucket: InvoiceListingBucket = {
      data: [],
      id: invoiceId,
      title: 'Invoice Level',
      type: ChargeAttachType.Invoice,
    };

    let currentBucket: InvoiceListingBucket | null = null;

    rows.forEach(row => {
      if (row.type === QuoteLineItemType.Equipment) {
        const equipmentId = row.equipment?.equipmentId;

        if (!equipmentId) {
          invoiceBucket.data.push(row);
          currentBucket = null;
          return;
        }

        currentBucket = {
          data: [row],
          id: equipmentId,
          type: ChargeAttachType.Equipment,
          title: row.equipment ? generateEquipmentName(row.equipment) : 'Equipment',
        };

        equipmentBuckets.push(currentBucket);
        return;
      }

      const attachId = this.getEquipmentBucketId(row);

      if (attachId) {
        if (currentBucket && attachId === currentBucket.id) {
          currentBucket.data.push(row);
          return;
        }

        const targetBucket = equipmentBuckets.find(bucket => bucket.id === attachId);

        if (targetBucket) {
          targetBucket.data.push(row);
          return;
        }
      }

      invoiceBucket.data.push(row);
    });

    return [...equipmentBuckets, invoiceBucket];
  }

  public getInsertIndex(rows: QuoteChargeListingEntity[], bucket: ChargeBucketRef): number {
    if (bucket.type !== ChargeAttachType.Equipment) return rows.length;

    const targetId = bucket.id;
    let equipmentIdx = -1;
    let lastAttachedIdx = -1;

    rows.forEach((row, index) => {
      if (row.type === QuoteLineItemType.Equipment && row.equipment?.equipmentId === targetId) {
        equipmentIdx = index;
        lastAttachedIdx = index;
        return;
      }

      if (equipmentIdx !== -1 && this.getEquipmentBucketId(row) === targetId) {
        lastAttachedIdx = index;
      }
    });

    return lastAttachedIdx === -1 ? rows.length : lastAttachedIdx + 1;
  }

  public buildParentOptions({ rows, currentRowIndex, scope }: BuildParentOptionsParams): ChargeParentOption[] {
    const options: ChargeParentOption[] = [];

    if (scope.type === ChargeAttachType.Equipment) {
      const equipmentRow = rows.find(
        row => row.type === QuoteLineItemType.Equipment && row.equipment?.equipmentId === scope.id
      );

      if (equipmentRow?.equipment) {
        options.push({
          kind: 'equipment',
          value: equipmentRow.equipment.equipmentId,
          label: generateEquipmentName(equipmentRow.equipment),
        });
      }
    }

    rows.forEach((row, index) => {
      if (index === currentRowIndex) return;
      if (row.type !== QuoteLineItemType.Invoice) return;

      const charge = row.charge;
      if (!charge?.id || !charge?.label || !charge?.groupBucket) return;
      if (charge.customerVisibility === CustomerVisibility.InternalOnly) return;
      if (!this.isChargeInScope(charge, scope)) return;

      options.push({ kind: 'charge', value: charge.id, label: charge.label });
    });

    return options;
  }

  public createCustomChargeRow(bucket: ChargeBucketRef, name: string): QuoteChargeListingEntity {
    return {
      equipment: null,
      type: QuoteLineItemType.Invoice,
      charge: {
        id: uuidv4(),
        label: name,
        parentId: null,
        price: newChargeRow,
        groupBucket: bucket,
        financeType: 'other',
        type: QuoteFeeType.Other,
        customerVisibility: CustomerVisibility.Visible,
      },
    };
  }

  public createChargeRowFromTemplate(
    bucket: ChargeBucketRef,
    template: Omit<Charge, 'groupBucket'>
  ): QuoteChargeListingEntity {
    return {
      equipment: null,
      type: QuoteLineItemType.Invoice,
      charge: {
        ...template,
        id: uuidv4(),
        parentId: null,
        groupBucket: bucket,
        customerVisibility: CustomerVisibility.Visible,
        financeType: chargeTypesByLabels[template.label],
      },
    };
  }

  private getEquipmentBucketId(row: QuoteChargeListingEntity) {
    const bucket = row.charge?.groupBucket;

    if (!bucket || bucket.type !== ChargeAttachType.Equipment) return null;

    return bucket.id ?? null;
  }

  private isChargeInScope(charge: Charge, scope: ChargeBucketRef): boolean {
    const { groupBucket } = charge;

    return groupBucket.type === scope.type && groupBucket.id === scope.id;
  }
}
